import { z } from 'zod';
import { STAFF_ROLES, STAFF_STATUSES } from '../auth/roles.js';
import { PageQuerySchema } from './pagination.js';

export const StaffSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.enum(STAFF_ROLES),
  status: z.enum(STAFF_STATUSES),
  /** False until the person has set their first password from the invitation. */
  passwordSet: z.boolean(),
  lastSignInAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type Staff = z.infer<typeof StaffSchema>;

export const CreateStaffSchema = z.object({
  name: z.string().trim().min(1).max(200),
  email: z.email().max(320),
  role: z.enum(STAFF_ROLES),
});
export type CreateStaff = z.infer<typeof CreateStaffSchema>;

export const UpdateStaffSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    role: z.enum(STAFF_ROLES).optional(),
  })
  .refine((v) => v.name !== undefined || v.role !== undefined, 'Nothing to update');
export type UpdateStaff = z.infer<typeof UpdateStaffSchema>;

/** Filters for the activity feed. `actorRole` narrows within the roles the viewer may see. */
export const ActivityQuerySchema = z
  .object({
    actorRole: z.enum(STAFF_ROLES).optional(),
    actorId: z
      .array(z.string())
      .or(z.string().transform((s) => [s]))
      .optional(),
    action: z.string().max(100).optional(),
    entityType: z.string().max(50).optional(),
    entityId: z.string().max(100).optional(),
    from: z.iso.datetime({ offset: true }).optional(),
    to: z.iso.datetime({ offset: true }).optional(),
  })
  .extend(PageQuerySchema.shape);
export type ActivityQuery = z.infer<typeof ActivityQuerySchema>;

export const ActivityEntrySchema = z.object({
  id: z.string(),
  at: z.string(),
  actor: z.object({ id: z.string(), name: z.string(), role: z.string() }).nullable(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  before: z.unknown(),
  after: z.unknown(),
  meta: z.unknown(),
});
export type ActivityEntry = z.infer<typeof ActivityEntrySchema>;

/** One staff member's card on the activity page. */
export const ActivitySummarySchema = z.object({
  actorId: z.string(),
  name: z.string(),
  role: z.enum(STAFF_ROLES),
  actionsThisWeek: z.number(),
  entitiesTouchedThisWeek: z.number(),
  lastActiveAt: z.string().nullable(),
});
export type ActivitySummary = z.infer<typeof ActivitySummarySchema>;
